import {ActionMenu} from '@primer/react-brand'
import {useEffect, useMemo, useState} from 'react'
import {getOS, type OS, type NavigatorUserAgent} from '@github-ui/get-os'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

export interface InstallerItem {
  platform: OS
  arch?: 'x86' | 'arm'
  label: string
  href: string
}

export interface InstallerActionMenuProps {
  items: InstallerItem[]
}

export const InstallerActionMenu = ({items}: InstallerActionMenuProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const [resolvedArch, setResolvedArch] = useState<'x86' | 'arm' | undefined>(undefined)

  const resolvedOS: OS = useMemo(() => {
    if (!isMounted) return 'Windows'

    const os = getOS()
    return os.os === 'macOS' ? 'macOS' : os.os === 'Windows' ? 'Windows' : 'Linux'
  }, [isMounted])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true)
  }, [])

  useEffect(() => {
    const uaData = (ssrSafeWindow?.navigator as (Navigator & NavigatorUserAgent) | undefined)?.userAgentData
    if (!uaData?.getHighEntropyValues) return

    let cancelled = false
    const detectArch = async () => {
      try {
        const values = await uaData.getHighEntropyValues(['architecture'])
        if (cancelled) return
        if (values.architecture === 'arm') setResolvedArch('arm')
        else if (values.architecture === 'x86') setResolvedArch('x86')
      } catch {
        // Architecture detection is best-effort; ignore failures.
      }
    }
    void detectArch()

    return () => {
      cancelled = true
    }
  }, [])

  const resolvedInstaller = useMemo(() => {
    const osItems = items.filter(item => item.platform === resolvedOS)
    if (resolvedArch) {
      const archMatch = osItems.find(item => item.arch === resolvedArch)
      if (archMatch) return archMatch
    }
    return osItems[0]
  }, [items, resolvedOS, resolvedArch])

  const sortedInstallerItems = useMemo(() => {
    const platformOrder = new Map<OS, number>()
    for (const item of items) {
      if (!platformOrder.has(item.platform)) platformOrder.set(item.platform, platformOrder.size)
    }
    // Prefer the user's detected architecture; fall back to the most common arch per platform.
    const archRank = (platform: OS, arch?: 'x86' | 'arm') => {
      const preferred = resolvedArch ?? (platform === 'macOS' ? 'arm' : 'x86')
      return arch === preferred ? 0 : arch ? 1 : 2
    }
    return items.slice().sort((a, b) => {
      // Preferred OS first
      const osRank = (platform: OS) => (platform === resolvedOS ? 0 : 1)
      if (osRank(a.platform) !== osRank(b.platform)) return osRank(a.platform) - osRank(b.platform)
      // Keep each platform's items grouped together, preserving the data's platform order
      if (a.platform !== b.platform) return platformOrder.get(a.platform)! - platformOrder.get(b.platform)!
      // Within a platform, order by arch (detected/preferred arch first, then other, then no arch)
      return archRank(a.platform, a.arch) - archRank(b.platform, b.arch)
    })
  }, [items, resolvedOS, resolvedArch])

  return (
    <ActionMenu mode="split-button">
      <ActionMenu.Button variant="primary" as="a" href={resolvedInstaller?.href}>
        {resolvedInstaller?.label}
      </ActionMenu.Button>
      <ActionMenu.Overlay aria-label={resolvedInstaller?.label || ''}>
        {sortedInstallerItems.map(item => (
          <ActionMenu.Item key={item.label} as="a" href={item.href}>
            {item.label}
          </ActionMenu.Item>
        ))}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
